import React, { useState } from 'react';
import {
  Briefcase,
  X,
  User,
  Sparkles,
  Save,
  Clock
} from 'lucide-react';
import { PracticeClient, PracticeCase, PriorityLevel } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';
import { createPracticeCase } from '../../lib/practiceManagementService';

interface NewCaseModalProps {
  clients: PracticeClient[];
  preselectedClient?: PracticeClient | null;
  lang: Language;
  onClose: () => void;
  onCaseCreated: (caseItem: PracticeCase) => void;
}

const THEME_OPTIONS = [
  { key: 'CAREER_BUSINESS', labelEn: 'Career, Profession & Business', labelHi: 'आजीविका, पदोन्नति एवं व्यापार' },
  { key: 'FINANCIAL_PROSPERITY', labelEn: 'Wealth, Assets & Prosperity', labelHi: 'धन, संपत्ति एवं समृद्धि' },
  { key: 'MARRIAGE_RELATIONSHIP', labelEn: 'Marriage & Relationship Compatibility', labelHi: 'विवाह, वैवाहिक सुख एवं संबंध' },
  { key: 'HEALTH_LONGEVITY', labelEn: 'Health & Vitality', labelHi: 'स्वास्थ्य एवं जीवन शक्ति' },
  { key: 'EDUCATION_KNOWLEDGE', labelEn: 'Education & Higher Learning', labelHi: 'विद्या, उच्च शिक्षा एवं ज्ञान' },
  { key: 'CHILDREN_PROGENY', labelEn: 'Children & Family Lineage', labelHi: 'संतान एवं कुल वृद्धि' },
  { key: 'FOREIGN_TRAVEL', labelEn: 'Foreign Relocation & Travel', labelHi: 'विदेश गमन एवं निवास' },
  { key: 'SPIRITUAL_GROWTH', labelEn: 'Spiritual Evolution & Dharma', labelHi: 'आध्यात्मिक साधना एवं मोक्ष' }
];

export const NewCaseModal: React.FC<NewCaseModalProps> = ({
  clients,
  preselectedClient,
  lang,
  onClose,
  onCaseCreated
}) => {
  const isHi = lang === 'hi';
  const [selectedClientId, setSelectedClientId] = useState<string>(
    preselectedClient?.id || clients[0]?.id || ''
  );
  const [themeKey, setThemeKey] = useState<string>('CAREER_BUSINESS');
  const [targetQuestion, setTargetQuestion] = useState<string>('');
  const [priority, setPriority] = useState<PriorityLevel>('MEDIUM');
  const [assignedPractitioner, setAssignedPractitioner] = useState<string>('Acharya V. Sharma');
  const [notes, setNotes] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetQuestion.trim()) return;

    const client = clients.find((c) => c.id === selectedClientId) || clients[0];
    const theme = THEME_OPTIONS.find((t) => t.key === themeKey) || THEME_OPTIONS[0];

    const newCase = createPracticeCase({
      clientId: client.id,
      clientRefId: client.referenceId,
      clientName: client.name,
      themeKey,
      themeTitleEn: theme.labelEn,
      themeTitleHi: theme.labelHi,
      targetQuestion: targetQuestion.trim(),
      stage: 'NEW',
      priority,
      assignedPractitioner,
      practitionerPrivateNotes: notes,
      clientFacingSummaryNotes: '',
      deterministicFindings: {
        natalPromiseVerdict: 'Natal capacity assessed from planetary alignment.',
        keySupportingFactors: ['Operating dasha lord confirmed', 'Ashtakvarga points favorable'],
        primaryDasha: client.activeDasha,
        primaryTransitTrigger: 'Upcoming Gochar trigger',
        projectedFructificationAge: `Age ${client.ageYears}`,
        vargaAffirmation: 'Divisional chart confirms'
      },
      timingWindowsSummary: 'Assessment in progress during intake phase.',
      prescribedRemedies: [],
      nextAction: 'Initiate Visharad (Pro) evidence analysis'
    });

    onCaseCreated(newCase);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-astro-primary/60 backdrop-blur-xs">
      <div className="bg-white dark:bg-slate-850 w-full max-w-lg rounded-2xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-4 bg-astro-secondary-surface dark:bg-astro-primary border-b border-astro-border dark:border-astro-strong-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-amber-500" />
            <h3 className="text-sm font-black text-astro-text dark:text-astro-muted">
              {isHi ? 'नया ज्योतिष केस / प्रकरण' : 'Open New Case / Reading'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-astro-muted hover:text-astro-secondary-text dark:hover:text-astro-muted cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-3.5 text-xs">
          <div className="space-y-1">
            <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
              Select Client *:
            </label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className="w-full p-2.5 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted font-bold"
            >
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.referenceId}) • {c.lagnaSign}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
              Primary Life Theme *:
            </label>
            <select
              value={themeKey}
              onChange={(e) => setThemeKey(e.target.value)}
              className="w-full p-2.5 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
            >
              {THEME_OPTIONS.map((t) => (
                <option key={t.key} value={t.key}>
                  {isHi ? t.labelHi : t.labelEn}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
              Client Target Question / Focal Inquiry *:
            </label>
            <textarea
              required
              rows={3}
              value={targetQuestion}
              onChange={(e) => setTargetQuestion(e.target.value)}
              placeholder="e.g. When is the most auspicious window for business expansion or career change?"
              className="w-full p-2.5 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="font-bold text-astro-secondary-text dark:text-astro-muted">Priority:</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as PriorityLevel)}
                className="w-full p-2 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
              >
                <option value="HIGH">High Priority</option>
                <option value="MEDIUM">Standard</option>
                <option value="LOW">Routine</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
                Assigned Practitioner:
              </label>
              <input
                type="text"
                value={assignedPractitioner}
                onChange={(e) => setAssignedPractitioner(e.target.value)}
                className="w-full p-2 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
              />
            </div>
          </div>

          <div className="p-3 bg-astro-secondary-surface dark:bg-astro-primary border-t border-astro-border dark:border-astro-strong-border flex items-center justify-end gap-2 -mx-5 -mb-5 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 rounded-lg bg-astro-card dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted font-bold text-xs cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <Save className="w-3.5 h-3.5" />
              <span>Initialize Case</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
